import { useEffect, useMemo, useState } from "react";
import { api, fmt, formatErr, downloadCSV, downloadPDF, whatsappShare } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, FileText, FileSpreadsheet, MessageCircle, Search,
  Filter as FilterIcon, ChevronDown, ChevronRight, Printer
} from "lucide-react";
import { PageHeader, ExportBar } from "@/components/EntityPage";

const FIELDS = [
  { key: "sl_no", label: "Sl No" },
  { key: "vehicle_name", label: "Vehicle Name", tone: "sky" },
  { key: "company_name", label: "Company Name" },
  { key: "email_id", label: "Email ID" },
  { key: "entry_date", label: "Entry Date" },
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "no_of_days", label: "No. of Days" },
  { key: "vehicle_type", label: "Vehicle Type" },
  { key: "vehicle_taken", label: "Vehicle Taken" },
  { key: "vehicle_given", label: "Vehicle Given" },
  { key: "trip_value", label: "Trip Value", money: true, tone: "sky" },
  { key: "adv_amount_paid", label: "Advance Paid", money: true },
  { key: "payable_amount", label: "Payable Amount", money: true, tone: "rose" },
  { key: "receivable_amount", label: "Receivable Amount", money: true, tone: "sky" },
  { key: "direct_pay_to_vendor", label: "Direct Pay → Vendor", money: true },
  { key: "direct_received_from_vendor", label: "Direct Received ← Vendor", money: true },
  { key: "net_payable", label: "Remaining Payable", money: true, tone: "rose" },
  { key: "net_receivable", label: "Remaining Receivable", money: true, tone: "emerald" },
  { key: "total", label: "Total (+/-)", money: true, tone: "pl" },
  { key: "remarks", label: "Remarks" },
];

function cellText(row, c) {
  const v = row[c.key];
  if (v === undefined || v === null || v === "") return "—";
  if (c.money) return fmt(v);
  return String(v);
}
function toneClass(c, row) {
  if (c.tone === "sky") return "text-sky-300 font-mono";
  if (c.tone === "rose") return "text-rose-300 font-mono";
  if (c.tone === "emerald") return "text-emerald-300 font-mono";
  if (c.tone === "pl") return `font-mono ${(row.total || 0) >= 0 ? "text-emerald-300" : "text-red-300"}`;
  if (c.money) return "font-mono";
  return "";
}
function rowClass(r) {
  const pay = (r.net_payable || 0) > 0;
  const recv = (r.net_receivable || 0) > 0;
  if (pay && recv) return "bg-amber-500/[0.06] border-l-4 border-l-amber-500/60";
  if (pay) return "bg-rose-500/[0.06] border-l-4 border-l-rose-500/60";
  if (recv) return "bg-emerald-500/[0.06] border-l-4 border-l-emerald-500/60";
  return "bg-transparent border-l-4 border-l-transparent";
}

function VaForm({ initial, refs, onSubmit }) {
  const [f, setF] = useState(initial || {});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (k) => (e) => set(k, parseFloat(e.target.value) || 0);
  const days = useMemo(() => {
    if (!f.start_date || !f.end_date) return 0;
    const [sy, sm, sd] = f.start_date.split("-").map(Number);
    const [ey, em, ed] = f.end_date.split("-").map(Number);
    const s = new Date(sy, sm - 1, sd), e = new Date(ey, em - 1, ed);
    return Math.max(1, Math.round((e - s) / (86400000)) + 1);
  }, [f.start_date, f.end_date]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendor</Label>
          <Select value={f.vendor_id || ""} onValueChange={(v) => { const vn = refs.vendors.find((x) => x.id === v); set("vendor_id", v); if (vn && !f.company_name) set("company_name", vn.name); }}>
            <SelectTrigger data-testid="va-vendor" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select vendor"/></SelectTrigger>
            <SelectContent>{refs.vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle</Label>
          <Select value={f.vehicle_id || ""} onValueChange={(v) => { const veh = refs.vehicles.find((x) => x.id === v); set("vehicle_id", v); if (veh) { set("vehicle_name", veh.vehicle_name); set("vehicle_type", veh.vehicle_type); } }}>
            <SelectTrigger data-testid="va-vehicle" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue placeholder="Select vehicle"/></SelectTrigger>
            <SelectContent>{refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no} · {v.vehicle_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle name</Label>
          <Input value={f.vehicle_name || ""} onChange={(e) => set("vehicle_name", e.target.value)} data-testid="va-vname" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle type</Label>
          <Input value={f.vehicle_type || ""} onChange={(e) => set("vehicle_type", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Company name</Label>
          <Input value={f.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="va-company" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email ID</Label>
          <Input type="email" value={f.email_id || ""} onChange={(e) => set("email_id", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Entry date</Label>
          <Input type="date" value={f.entry_date || ""} onChange={(e) => set("entry_date", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start date</Label>
          <Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="va-start" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">End date</Label>
          <Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="va-end" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">No. of days (auto)</Label>
          <Input value={days || ""} readOnly className="bg-[hsl(224_40%_12%)] mt-1 opacity-70"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle taken from</Label>
          <Input value={f.vehicle_taken || ""} onChange={(e) => set("vehicle_taken", e.target.value)} placeholder="e.g. taken from vendor" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle given to</Label>
          <Input value={f.vehicle_given || ""} onChange={(e) => set("vehicle_given", e.target.value)} placeholder="e.g. given to vendor" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          ["trip_value","Trip value ₹"],["adv_amount_paid","Adv paid ₹"],
          ["payable_amount","Payable amount ₹"],["receivable_amount","Receivable amount ₹"],
          ["direct_pay_to_vendor","Direct pay → vendor ₹"],["direct_received_from_vendor","Direct received ← vendor ₹"],
        ].map(([k, l]) => (
          <div key={k}><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</Label>
            <Input type="number" value={f[k] || 0} onChange={num(k)} data-testid={`va-${k}`} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
        ))}
      </div>
      <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Remarks</Label>
        <Input value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="bg-[hsl(224_40%_12%)] mt-1"/></div>
      <div className="flex justify-end">
        <Button type="submit" data-testid="va-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Save</Button>
      </div>
    </form>
  );
}

function Row({ r, onEdit, onDelete, onPrint }) {
  const [open, setOpen] = useState(false);
  const summary = [
    { label: "Sl No", val: r.sl_no || "—", cls: "font-mono text-amber-300" },
    { label: "Vendor", val: r.vendor_name || r.company_name || "—" },
    { label: "Vehicle", val: r.vehicle_name || "—", cls: "text-sky-300 font-mono" },
    { label: "Days", val: r.no_of_days || 0, cls: "font-mono" },
    { label: "Trip Value", val: fmt(r.trip_value), cls: "text-sky-300 font-mono" },
    { label: "Total", val: (r.total >= 0 ? "+" : "") + fmt(r.total),
      cls: `font-mono ${r.total >= 0 ? "text-emerald-300" : "text-red-300"}` },
  ];
  return (
    <div className={`rounded-lg border border-border transition-colors ${rowClass(r)}`} data-testid={`va-row-${r.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex flex-col md:grid md:grid-cols-[auto_1fr_auto] gap-3 md:items-center md:gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-slate-400 shrink-0">{open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</div>
          <div className="min-w-0 flex-1 overflow-x-auto -mx-1 px-1 [scrollbar-width:thin]">
            <div className="grid grid-cols-6 gap-3 items-center min-w-[640px] md:min-w-0">
              {summary.map((s) => (
                <div key={s.label} className="min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{s.label}</div>
                  <div className={`text-sm truncate ${s.cls || ""}`}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap md:justify-end shrink-0 border-t border-border/40 md:border-0 pt-2 md:pt-0">
          {r.net_payable > 0 && <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40">Pay · {fmt(r.net_payable)}</Badge>}
          {r.net_receivable > 0 && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Recv · {fmt(r.net_receivable)}</Badge>}
          {!r.net_payable && !r.net_receivable && <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/30">Settled</Badge>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-border/60">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 pt-3">
            {FIELDS.map((c) => (
              <div key={c.key} className="grid grid-cols-[150px_1fr] items-center py-1 border-b border-border/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                <div className={`text-sm ${toneClass(c, r)}`}>{cellText(r, c)}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-3 justify-end">
            <Button variant="outline" size="sm" onClick={() => onPrint(r)} data-testid={`va-slip-${r.id}`} className="gap-2 border-border bg-[hsl(224_40%_12%)]"><Printer className="w-3.5 h-3.5"/> Slip PDF</Button>
            <Button variant="outline" size="sm" onClick={() => onEdit(r)} data-testid={`edit-va-${r.id}`} className="gap-2 border-border bg-[hsl(224_40%_12%)]"><Pencil className="w-3.5 h-3.5"/> Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(r.id)} data-testid={`del-va-${r.id}`} className="gap-2 border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5"/> Delete</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function slipPDF(r) {
  const w = window.open("", "_blank");
  const style = `<style>body{font-family:sans-serif;padding:24px;color:#0b1220}h1{margin:0 0 4px}.sub{color:#64748b;font-size:12px;margin-bottom:16px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}th{background:#f8fafc;width:38%;color:#334155;font-weight:600}</style>`;
  const rows = FIELDS.map((c) => `<tr><th>${c.label}</th><td>${cellText(r, c)}</td></tr>`).join("");
  w.document.write(`<html><head><title>Vendor Slip ${r.sl_no || ""}</title>${style}</head><body>
    <h1>Vendor Ledger Slip · #${r.sl_no || ""}</h1><div class="sub">Ashok Netravels · ${r.entry_date || ""}</div>
    <table>${rows}</table>
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`);
  w.document.close();
}

function ledgerPDF(rows) {
  if (!rows.length) return;
  const w = window.open("", "_blank");
  const style = `<style>body{font-family:sans-serif;padding:20px;color:#0b1220}h1{margin:0 0 10px}table{border-collapse:collapse;width:100%;font-size:10px;margin-bottom:18px;page-break-inside:avoid}th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;vertical-align:top}th{background:#f1f5f9;width:38%;color:#334155;font-weight:600}h2{font-size:14px;margin:20px 0 6px;color:#0f172a;border-bottom:2px solid #f59e0b;padding-bottom:4px}</style>`;
  const sections = rows.map((r) => {
    const body = FIELDS.map((c) => `<tr><th>${c.label}</th><td>${cellText(r, c)}</td></tr>`).join("");
    return `<h2>#${r.sl_no || ""} · ${r.company_name || r.vendor_name || ""} · ${r.entry_date || ""}</h2><table>${body}</table>`;
  }).join("");
  w.document.write(`<html><head><title>Vendor Ledger</title>${style}</head><body>
    <h1>Ashok Netravels · Vendor Accounting (${rows.length})</h1>${sections}
    <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
  </body></html>`);
  w.document.close();
}

export default function VendorAccounting() {
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({ vendors: [], vehicles: [] });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fVendor, setFVendor] = useState("all");
  const [fCompany, setFCompany] = useState("all");
  const [fVehicle, setFVehicle] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  async function loadAll() {
    const [a, v, veh] = await Promise.all([
      api.get("/vendor-accounts"), api.get("/vendors"), api.get("/vehicles"),
    ]);
    setRows(a.data); setRefs({ vendors: v.data, vehicles: veh.data });
  }
  useEffect(() => { loadAll(); }, []);

  const nameOf = (arr, id, k = "name") => arr.find((x) => x.id === id)?.[k] || "";

  const enriched = useMemo(() => rows.map((r) => ({
    ...r,
    vendor_name: nameOf(refs.vendors, r.vendor_id) || r.company_name || "",
  })), [rows, refs]);

  const companies = useMemo(() => {
    const s = new Set(enriched.map((r) => r.company_name).filter(Boolean));
    return [...s];
  }, [enriched]);

  const filtered = useMemo(() => enriched.filter((r) => {
    if (fVendor !== "all" && r.vendor_id !== fVendor) return false;
    if (fCompany !== "all" && r.company_name !== fCompany) return false;
    if (fVehicle !== "all" && r.vehicle_id !== fVehicle) return false;
    const d = (r.entry_date || r.start_date || "").slice(0, 10);
    if (fFrom && (!d || d < fFrom)) return false;
    if (fTo && (!d || d > fTo)) return false;
    if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [enriched, fVendor, fCompany, fVehicle, fFrom, fTo, q]);

  const totals = useMemo(() => filtered.reduce((a, r) => ({
    trip_value: a.trip_value + (r.trip_value || 0),
    received: a.received + (r.direct_received_from_vendor || 0),
    paid: a.paid + ((r.direct_pay_to_vendor || 0) + (r.adv_amount_paid || 0)),
    net_receivable: a.net_receivable + (r.net_receivable || 0),
    net_payable: a.net_payable + (r.net_payable || 0),
    total: a.total + (r.total || 0),
  }), { trip_value: 0, received: 0, paid: 0, net_receivable: 0, net_payable: 0, total: 0 }), [filtered]);

  // Month-wise breakdown
  const monthly = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const m = (r.entry_date || r.start_date || "").slice(0, 7);
      if (!m) return;
      if (!map[m]) map[m] = { month: m, trips: 0, trip_value: 0, received: 0, paid: 0, net_receivable: 0, net_payable: 0, total: 0 };
      map[m].trips += 1;
      map[m].trip_value += r.trip_value || 0;
      map[m].received += r.direct_received_from_vendor || 0;
      map[m].paid += (r.direct_pay_to_vendor || 0) + (r.adv_amount_paid || 0);
      map[m].net_receivable += r.net_receivable || 0;
      map[m].net_payable += r.net_payable || 0;
      map[m].total += r.total || 0;
    });
    return Object.values(map).sort((a, b) => b.month.localeCompare(a.month));
  }, [filtered]);

  async function save(f) {
    try {
      const v = refs.vendors.find((x) => x.id === f.vendor_id);
      const payload = { ...f, vendor_name: v?.name };
      if (editing) await api.put(`/vendor-accounts/${editing.id}`, payload);
      else await api.post("/vendor-accounts", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); loadAll();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/vendor-accounts/${id}`); loadAll(); toast.success("Deleted"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  const exportRows = filtered.map((r) => Object.fromEntries(FIELDS.map((c) => [c.label, cellText(r, c)])));
  const clearFilters = () => { setFVendor("all"); setFCompany("all"); setFVehicle("all"); setFFrom(""); setFTo(""); setQ(""); };

  return (
    <div>
      <PageHeader
        title="Vendor Accounting"
        subtitle="Row-wise vendor ledger with month-wise summary. Rows tint by remaining payable / receivable balance."
        right={
          <>
            <Button variant="outline" onClick={() => ledgerPDF(filtered)} data-testid="va-pdf" className="border-border bg-[hsl(224_40%_12%)] hover:bg-white/5 gap-2"><FileText className="w-4 h-4"/> Row-wise PDF</Button>
            <ExportBar rows={exportRows} filename="vendor-accounting" whatsappText={`Vendor accounting · ${filtered.length} entries · Net ₹${totals.total}`} />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-va-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><Plus className="w-4 h-4"/> New Entry</Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-3xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit vendor entry" : "New vendor entry"}</DialogTitle></DialogHeader>
                <VaForm initial={editing} refs={refs} onSubmit={save}/>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Total trip value</div>
          <div className="font-display text-2xl font-bold text-sky-300 mt-1">{fmt(totals.trip_value)}</div>
        </Card>
        <Card className="p-4 bg-emerald-500/[0.08] border-emerald-500/30" data-testid="va-total-received">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300">Total received</div>
          <div className="font-display text-2xl font-bold text-emerald-200 mt-1">{fmt(totals.received)}</div>
        </Card>
        <Card className="p-4 bg-rose-500/[0.08] border-rose-500/30" data-testid="va-remaining-payable">
          <div className="text-[11px] uppercase tracking-wider text-rose-300">Remaining payable → vendor</div>
          <div className="font-display text-2xl font-bold text-rose-200 mt-1">{fmt(totals.net_payable)}</div>
        </Card>
        <Card className="p-4 bg-sky-500/[0.08] border-sky-500/30" data-testid="va-remaining-receivable">
          <div className="text-[11px] uppercase tracking-wider text-sky-300">Remaining receivable ← vendor</div>
          <div className="font-display text-2xl font-bold text-sky-200 mt-1">{fmt(totals.net_receivable)}</div>
        </Card>
        <Card className={`p-4 border ${totals.total >= 0 ? "bg-emerald-500/[0.08] border-emerald-500/30" : "bg-red-500/[0.08] border-red-500/30"}`} data-testid="va-net">
          <div className={`text-[11px] uppercase tracking-wider ${totals.total >= 0 ? "text-emerald-300" : "text-red-300"}`}>Net position</div>
          <div className={`font-display text-2xl font-bold mt-1 ${totals.total >= 0 ? "text-emerald-200" : "text-red-200"}`}>{totals.total >= 0 ? "+" : ""}{fmt(totals.total)}</div>
        </Card>
      </div>

      <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-4">
        <div className="flex items-center gap-2 mb-4 text-amber-400">
          <FilterIcon className="w-4 h-4"/><div className="text-xs uppercase tracking-[0.2em]">Filters</div>
          <button onClick={clearFilters} data-testid="va-clear-filters" className="ml-auto text-[11px] text-amber-300 hover:underline">Clear all</button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Vendor</Label>
            <Select value={fVendor} onValueChange={setFVendor}>
              <SelectTrigger data-testid="va-f-vendor" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {refs.vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Company</Label>
            <Select value={fCompany} onValueChange={setFCompany}>
              <SelectTrigger data-testid="va-f-company" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All companies</SelectItem>
                {companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Vehicle</Label>
            <Select value={fVehicle} onValueChange={setFVehicle}>
              <SelectTrigger data-testid="va-f-vehicle" className="bg-[hsl(224_40%_12%)] mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vehicles</SelectItem>
                {refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_no}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">From date</Label>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="va-f-from" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">To date</Label>
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="va-f-to" className="bg-[hsl(224_40%_12%)] mt-1"/>
          </div>
        </div>
      </Card>

      {monthly.length > 0 && (
        <Card className="p-5 bg-[hsl(224_45%_9%)] border-border mb-4">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-3">Month-wise breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
                {["Month","Entries","Trip value","Received","Paid","Remaining Payable","Remaining Receivable","Total (+/-)"].map((h) => <th key={h} className="text-left p-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {monthly.map((m) => (
                  <tr key={m.month} className="border-t border-border">
                    <td className="p-3 font-mono">{m.month}</td>
                    <td className="p-3 font-mono">{m.trips}</td>
                    <td className="p-3 font-mono text-sky-300">{fmt(m.trip_value)}</td>
                    <td className="p-3 font-mono text-emerald-300">{fmt(m.received)}</td>
                    <td className="p-3 font-mono">{fmt(m.paid)}</td>
                    <td className="p-3 font-mono text-rose-300">{fmt(m.net_payable)}</td>
                    <td className="p-3 font-mono text-sky-300">{fmt(m.net_receivable)}</td>
                    <td className={`p-3 font-mono ${m.total >= 0 ? "text-emerald-300" : "text-red-300"}`}>{m.total >= 0 ? "+" : ""}{fmt(m.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-4 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text…" data-testid="va-search" className="bg-transparent border-0 focus-visible:ring-0 h-8 max-w-xs"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No records match your filters</div>
          ) : filtered.map((r) => (
            <Row key={r.id} r={r} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={del} onPrint={slipPDF}/>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-rose-500/60 bg-rose-500/10"/> Payable to vendor</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-emerald-500/60 bg-emerald-500/10"/> Receivable from vendor</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-amber-500/60 bg-amber-500/10"/> Both</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border-l-4 border-transparent bg-white/[0.03]"/> Settled</span>
        </div>
      </Card>
    </div>
  );
}
